import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseObject } from '@/types/shared';

/**
 * `/auth/me` — the identity + department bootstrap. Works with the plain
 * Keycloak bearer (NOT UMA-gated), so it always returns even while row-scoped
 * list endpoints stay empty pending the UMA authorization token. Source of the
 * signed-in employee's department, used to scope the dashboard.
 */
export interface MeEmployee {
  employee_id: number;
  employee_name: string;
  employee_email?: string;
  employee_email_company?: string;
  department_name?: string;
  /** Job title, e.g. "TECHNOLOGY LEADER" (legacy shows position • department). */
  position_name?: string;
  title?: string;
}

export interface Me {
  employee: MeEmployee;
  is_admin?: boolean;
  /**
   * Role guard: when true the signed-in employee may filter the customer
   * calendar-events list by follower. Mirrors the legacy
   * `can_filter_customer_event_followers` field from `/auth/me`.
   */
  can_filter_customer_event_followers?: boolean;
  /** UMA authorization payload — null until the RPT is obtained. */
  authorization: unknown;
}

export const meKeys = { me: ['me'] as const };

export function useMe() {
  return useQuery({
    queryKey: meKeys.me,
    queryFn: async (): Promise<Me> => {
      const res = await dataCentralApi.get<ResponseObject<Me>>('/auth/me');
      return res.data.result;
    },
    staleTime: 5 * 60_000,
  });
}
